import { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Building2, Users, FileText, CheckCircle, XCircle, Clock,
  ArrowLeft, TrendingUp, Shield, Database, Globe, Calendar,
  BarChart3, PieChart, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#3B82F6'];

const TenantDashboardPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [dashboard, setDashboard] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, [tenantId]);

  const fetchDashboard = async () => {
    try {
      const [dashboardRes, tenantRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tenants/${tenantId}/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tenants/${tenantId}`, { withCredentials: true })
      ]);
      setDashboard(dashboardRes.data);
      setTenant(tenantRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erro ao carregar dashboard do tenant');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  if (!dashboard || !tenant) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Tenant não encontrado</p>
        <Button onClick={() => navigate('/tenants')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  const stats = dashboard.statistics;
  
  const consentsPieData = [
    { name: 'Ativos', value: stats.active_consents, color: '#10B981' },
    { name: 'Pendentes', value: stats.pending_consents, color: '#F59E0B' },
    { name: 'Revogados', value: stats.revoked_consents, color: '#EF4444' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8" data-testid="tenant-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tenants')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#10B981]" />
              <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {dashboard.tenant.name}
              </h1>
              <Badge className={tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {tenant.is_active ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            <p className="text-slate-600 flex items-center gap-2 mt-1">
              <Globe className="w-4 h-4" />
              {dashboard.tenant.domain}
              <span className="mx-2">•</span>
              <Database className="w-4 h-4" />
              <span className="font-mono text-sm">{tenant.database_name}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/tenants`)}>
            <Users className="w-4 h-4 mr-2" />
            Ver Utilizadores
          </Button>
        </div>
      </div>

      {/* Compliance Score Banner */}
      <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm mb-1">Score de Conformidade RGPD</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold">{stats.compliance_score}%</span>
                <Shield className="w-8 h-8 text-emerald-200" />
              </div>
              <p className="text-emerald-100 text-sm mt-2">
                Taxa de Consentimentos Ativos: {stats.active_rate}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-emerald-100 text-sm">Total de Consentimentos</p>
              <p className="text-4xl font-bold">{stats.total_consents}</p>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={stats.compliance_score} className="h-2 bg-emerald-400" />
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Utilizadores</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Titulares de Dados</p>
                <p className="text-3xl font-bold text-purple-600">{stats.total_titulares}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Consentimentos Ativos</p>
                <p className="text-3xl font-bold text-green-600">{stats.active_consents}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pendentes / Revogados</p>
                <p className="text-3xl font-bold text-amber-600">
                  {stats.pending_consents} / {stats.revoked_consents}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Distribuição de Consentimentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {consentsPieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={consentsPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {consentsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                Sem dados de consentimentos
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Resumo de Conformidade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Consentimentos Ativos</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{stats.active_consents}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <span>Aguardam Validação</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">{stats.pending_consents}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span>Revogados</span>
                </div>
                <span className="text-2xl font-bold text-red-600">{stats.revoked_consents}</span>
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between text-sm mb-2">
                  <span>Taxa de Conformidade</span>
                  <span className="font-bold">{stats.compliance_score}%</span>
                </div>
                <Progress value={stats.compliance_score} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Atividade Recente
          </CardTitle>
          <CardDescription>Últimos consentimentos registados</CardDescription>
        </CardHeader>
        <CardContent>
          {dashboard.recent_activity?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_activity.map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="font-medium text-sm">{activity.contact_value || 'N/A'}</p>
                      <p className="text-xs text-slate-500">{activity.contact_type} - {activity.purposes?.join(', ')}</p>
                    </div>
                  </div>
                  <Badge className={
                    activity.status === 'active' ? 'bg-green-100 text-green-800' :
                    activity.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }>
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Sem atividade recente
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-500">Criado em</p>
              <p className="font-medium">
                {new Date(dashboard.tenant.created_at).toLocaleDateString('pt-PT')}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Email de Contacto</p>
              <p className="font-medium">{tenant.contact_email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">DPO</p>
              <p className="font-medium">{tenant.dpo_email || 'Não definido'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Administradores</p>
              <p className="font-medium">{tenant.admins?.length || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantDashboardPage;
